import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { landingPagesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const PublicLandingPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const response = await landingPagesAPI.getPublic(slug);
      setPage(response.data.page);
    } catch (error) {
      console.error('Page not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await landingPagesAPI.submitLead(slug, formData);
      setThankYouMessage(response.data.message);
      setSubmitted(true);
      
      if (response.data.redirect_url) {
        setTimeout(() => {
          window.location.href = response.data.redirect_url;
        }, 2000);
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Page Not Found</h1>
          <p className="text-gray-400">This landing page does not exist or has been deactivated.</p>
        </div>
      </div>
    );
  }

  const themeStyles = {
    dark: {
      bg: 'bg-void',
      text: 'text-white',
      muted: 'text-gray-400',
      card: 'bg-void-paper border-white/10',
      input: 'bg-void border-white/10 text-white'
    },
    light: {
      bg: 'bg-gray-50',
      text: 'text-gray-900',
      muted: 'text-gray-600',
      card: 'bg-white border-gray-200 shadow-lg',
      input: 'bg-white border-gray-300 text-gray-900'
    },
    gradient: {
      bg: 'bg-gradient-to-br from-void via-neon-violet/20 to-neon-mint/20',
      text: 'text-white',
      muted: 'text-gray-300',
      card: 'bg-void/80 backdrop-blur-xl border-white/10',
      input: 'bg-void/50 border-white/20 text-white'
    },
    modern: {
      bg: 'bg-void',
      text: 'text-white',
      muted: 'text-gray-400',
      card: 'bg-void-paper border-white/10',
      input: 'bg-void border-white/10 text-white'
    }
  };

  const theme = themeStyles[page.theme] || themeStyles.dark;
  const embedUrl = getYouTubeEmbedUrl(page.video_url);

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Header */}
      <header className="py-6 px-6">
        <div className="max-w-6xl mx-auto flex items-center gap-2">
          <div className="w-8 h-8 bg-neon-mint rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-black" />
          </div>
          <span className="font-heading font-bold text-white">TrafficFlow</span>
          <span className="font-heading font-bold text-neon-violet">AI</span>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <h1 className={`font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl ${theme.text} mb-6 leading-tight`}>
                {page.headline}
              </h1>
              
              {page.subheadline && (
                <p className={`text-xl ${theme.muted} mb-6`}>
                  {page.subheadline}
                </p>
              )}
              
              <p className={`text-lg ${theme.muted} mb-8 leading-relaxed`}>
                {page.description}
              </p>

              {/* Video */}
              {embedUrl && (
                <div className="mb-8 rounded-xl overflow-hidden aspect-video">
                  <iframe
                    src={embedUrl}
                    title="Video"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {/* Hero Image */}
              {page.hero_image_url && !embedUrl && (
                <div className="mb-8 rounded-xl overflow-hidden">
                  <img 
                    src={page.hero_image_url} 
                    alt={page.headline}
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>

            {/* Right: Form */}
            <div className={`${theme.card} rounded-2xl p-8 border`}>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-neon-mint/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-neon-mint" />
                  </div>
                  <h3 className={`font-heading font-bold text-2xl ${theme.text} mb-4`}>
                    Thank You!
                  </h3>
                  <p className={theme.muted}>
                    {thankYouMessage}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="text-center mb-6">
                    <h3 className={`font-heading font-bold text-xl ${theme.text}`}>
                      Get Started Now
                    </h3>
                    <p className={`${theme.muted} text-sm`}>
                      Fill in your details below
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className={theme.text}>Name</Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                      required
                      className={`h-12 ${theme.input}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className={theme.text}>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="you@example.com"
                      required
                      className={`h-12 ${theme.input}`}
                    />
                  </div>

                  {page.collect_phone && (
                    <div className="space-y-2">
                      <Label className={theme.text}>Phone (Optional)</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+1 (555) 000-0000"
                        className={`h-12 ${theme.input}`}
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-14 bg-neon-mint text-black hover:bg-neon-mint/90 font-bold text-lg shadow-neon"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {page.cta_text}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className={`text-center text-xs ${theme.muted}`}>
                    We respect your privacy. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 mt-12">
        <div className="max-w-6xl mx-auto text-center">
          <p className={`text-sm ${theme.muted}`}>
            Powered by <span className="text-neon-mint">TrafficFlow AI</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLandingPage;
